import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { getAnniversariesThisMonth } from "../../../../app/utils/common/dashboardEvents";
import { sendWorkAnniversaryEmail } from "../../../../app/utils/common/workAnniversaryEmail";
import { showErrorResponse } from "../../../../app/utils/common/Functions";
import { showSuccessToast } from "../../../../app/utils/common/Toast";
import DashboardWidgetProfilePhoto from "../DashboardWidgetProfilePhoto";

const UpcomingAnniversary = ({
  employees = [],
  canSendCelebrationEmail = false,
  sentAnniversaryEmailUserIds = [],
  onAnniversaryEmailSent,
}) => {
  const [sendingUserId, setSendingUserId] = useState(null);
  const today = moment().startOf("day");

  const monthLabel = useMemo(() => moment().format("MMMM YYYY"), []);

  const items = useMemo(() => getAnniversariesThisMonth(employees), [employees]);

  const sentIdSet = useMemo(
    () => new Set(sentAnniversaryEmailUserIds.map((id) => Number(id))),
    [sentAnniversaryEmailUserIds]
  );

  const handleSendAnniversaryEmail = useCallback(
    async (userId) => {
      if (!userId || sendingUserId || sentIdSet.has(Number(userId))) return;
      setSendingUserId(userId);
      try {
        const res = await sendWorkAnniversaryEmail(userId);
        showSuccessToast(res?.message || "Work anniversary email sent.");
        if (typeof onAnniversaryEmailSent === "function") {
          onAnniversaryEmailSent(userId);
        }
      } catch (err) {
        showErrorResponse(err);
        const msg =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          "";
        if (/already been sent/i.test(String(msg)) && typeof onAnniversaryEmailSent === "function") {
          onAnniversaryEmailSent(userId);
        }
      } finally {
        setSendingUserId(null);
      }
    },
    [sendingUserId, sentIdSet, onAnniversaryEmailSent]
  );

  return (
    <div className="dashboard-widget dashboard-widget--anniversaries">
      <div className="dashboard-widget-header">
        <div className="dashboard-widget-header-left">
          <span className="dashboard-widget-icon-wrap">
            <img src="/svg/Icons/praise-icon.svg" alt="" aria-hidden="true" />
          </span>
          <h4 className="dashboard-widget-title">Work anniversaries this month</h4>
        </div>
        <span className="dashboard-widget-meta">{monthLabel}</span>
      </div>
      <div className="dashboard-widget-body">
        {items.length === 0 ? (
          <div className="dashboard-widget-empty">
            <span className="dashboard-widget-empty-icon dashboard-widget-empty-icon--anniversary" aria-hidden="true">
              🎉
            </span>
            <p className="dashboard-widget-empty-text">No work anniversaries in {monthLabel}</p>
            <p className="dashboard-widget-empty-hint">Milestone celebrations will show up here</p>
          </div>
        ) : (
          <ul className="dashboard-widget-list">
            {items.map((row) => {
              const isToday = row.nextDate.isSame(today, "day");
              return (
                <li
                  key={`an-${row.userId}-${row.nextDate.format("YYYY-MM-DD")}`}
                  className="dashboard-widget-list-item"
                >
                  <DashboardWidgetProfilePhoto profileImage={row.profileImage} name={row.name} />
                  <div className="dashboard-widget-item-main">
                    <div className="dashboard-widget-item-name">{row.name}</div>
                    <div className="dashboard-widget-item-sub dashboard-widget-item-sub--anniversary">
                      {row.yearsOfService >= 1 ? (
                        <>
                          {row.yearsOfService} year{row.yearsOfService !== 1 ? "s" : ""}
                          {isToday ? <span> · Today</span> : row.isPast ? <span> · Completed</span> : null}
                        </>
                      ) : isToday ? (
                        <span>Starting today</span>
                      ) : row.isPast ? (
                        <span>Joined · Completed</span>
                      ) : (
                        <span>Joining soon</span>
                      )}
                    </div>
                  </div>
                  <div className="dashboard-widget-item-end">
                    <span className="dashboard-widget-date">
                      {row.nextDate.format("ddd, DD MMM")}
                    </span>
                    {canSendCelebrationEmail && isToday && !sentIdSet.has(Number(row.userId)) ? (
                      <button
                        type="button"
                        className="dashboard-widget-celebrate-btn dashboard-widget-celebrate-btn--anniversary"
                        disabled={sendingUserId === row.userId}
                        onClick={() => handleSendAnniversaryEmail(row.userId)}
                      >
                        {sendingUserId === row.userId ? "Sending…" : "Send email"}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UpcomingAnniversary;
